import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';
import { listOnboardingTasks, updateOnboardingTask } from '@src/api/onboardingWorkflow';
import { fetchClient } from '@src/api/actions/clients';
import paths from '@src/paths';

export default function OnboardingChecklistPage() {
  const { clientSlug } = useParams();
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notesDialog, setNotesDialog] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Creative reviews
  const [creativeReviews, setCreativeReviews] = useState([]);
  const [creativeUploadOpen, setCreativeUploadOpen] = useState(false);
  const [creativeAds, setCreativeAds] = useState([]);
  const [creativeVerticalId, setCreativeVerticalId] = useState('');
  const [creativeUploading, setCreativeUploading] = useState(false);

  // Client onboarding data (offers, assets)
  const [onboardingData, setOnboardingData] = useState(null);

  // Landing pages
  const [landingPages, setLandingPages] = useState([]);
  const [lpVerticals, setLpVerticals] = useState([]);
  const [lpDialogOpen, setLpDialogOpen] = useState(false);
  const [lpUrl, setLpUrl] = useState('');
  const [lpVerticalId, setLpVerticalId] = useState('');
  const [lpName, setLpName] = useState('');
  const [lpSaving, setLpSaving] = useState(false);

  // Selfbook calendar
  const [selfbookCalendar, setSelfbookCalendar] = useState(null);

  // Internal notes (for creative designer)
  const [internalNotes, setInternalNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const handleSaveInternalNotes = async () => {
    setNotesSaving(true);
    try {
      await defaultClient.request({
        url: `clients/${clientSlug}/onboarding/`,
        method: 'PATCH',
        payload: { internal_notes: internalNotes },
      });
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  };

  // Facebook campaign builder
  const [fbDialogOpen, setFbDialogOpen] = useState(false);
  const [fbAccountId, setFbAccountId] = useState('');
  const [fbPageId, setFbPageId] = useState('');
  const [fbBuilding, setFbBuilding] = useState(false);
  const [fbResult, setFbResult] = useState(null);

  const refreshCreative = () => {
    defaultClient.request({ url: `app1/creative-review/${clientSlug}/` })
      .then((res) => setCreativeReviews(res?.data?.results || []))
      .catch(() => {});
  };

  const refreshLandingPages = () => {
    if (!clientSlug) return;
    defaultClient.request({ url: `app1/landing-pages/${clientSlug}/` })
      .then((res) => {
        setLandingPages(res?.data?.results || []);
        setLpVerticals(res?.data?.verticals || []);
      })
      .catch(() => {});
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientRes, tlRes, onbRes] = await Promise.all([
        fetchClient(clientSlug),
        defaultClient.request({ url: `clients/${clientSlug}/onboarding/timeline/` }).catch(() => null),
        defaultClient.request({ url: `clients/${clientSlug}/onboarding/` }).catch(() => null),
      ]);
      const c = clientRes?.data;
      setClient(c);
      if (tlRes?.data) {
        setTimeline(tlRes.data);
        setSelfbookCalendar(tlRes.data.selfbook_calendar || null);
      }
      // Load internal notes from onboarding payload
      if (c) {
        defaultClient.request({ url: `clients/${clientSlug}/onboarding/` })
          .then((res) => {
            const notes = res?.data?.internal_notes || '';
            setInternalNotes(notes);
          })
          .catch(() => {});
      }
      if (onbRes?.data) setOnboardingData(onbRes.data);
      if (c?.id) {
        const taskRes = await listOnboardingTasks({ client: c.id });
        setTasks(taskRes?.data?.results || []);
      }
    } catch {
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  }, [clientSlug]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (clientSlug) { refreshCreative(); refreshLandingPages(); } }, [clientSlug]);

  const handleToggleComplete = async (task) => {
    const newStatus = task.status === 'complete' ? 'not_started' : 'complete';
    setSaving(true);
    try {
      await updateOnboardingTask(task.id, { status: newStatus });
      setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: newStatus } : t));
    } catch {
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!notesDialog) return;
    setSaving(true);
    try {
      await updateOnboardingTask(notesDialog, { notes: notesValue });
      setTasks((prev) => prev.map((t) => t.id === notesDialog ? { ...t, notes: notesValue } : t));
      setNotesDialog(null);
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleFbBuild = async (dryRun = false) => {
    if (!fbAccountId) { toast.error('Ad Account ID is required'); return; }
    setFbBuilding(true);
    setFbResult(null);
    try {
      const res = await defaultClient.request({
        url: 'app1/facebook/build-campaigns/',
        method: 'POST',
        payload: { client_slug: clientSlug, ad_account_id: fbAccountId.replace('act_', ''), page_id: fbPageId || '', dry_run: dryRun },
      });
      setFbResult(res?.data);
      if (!dryRun) { toast.success(`Built ${res?.data?.campaigns?.length || 0} campaigns!`); loadData(); }
    } catch (err) {
      setFbResult({ errors: [{ error: err?.response?.data?.detail || 'Failed to build campaigns' }] });
    } finally {
      setFbBuilding(false);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  // Group tasks by phase
  const phases = {};
  const parentTasks = tasks.filter((t) => !t.parent_template_key);
  parentTasks.forEach((t) => { const phase = t.phase || 'Other'; if (!phases[phase]) phases[phase] = []; phases[phase].push(t); });
  const totalTasks = parentTasks.length;
  const completedTasks = parentTasks.filter((t) => t.status === 'complete').length;
  const progress = totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const clientName = client?.dba_name || client?.name || clientSlug;

  // Timeline data
  const completionSteps = (timeline?.steps || []).filter((s) => s.key !== 'approvals');
  const clientActions = timeline?.client_actions || [];
  const estimatedLaunch = timeline?.estimated_launch;
  const launchReason = timeline?.estimated_launch_reason || '';

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h5" fontWeight={700}>Onboarding: {clientName}</Typography>
            <Chip label={client?.status?.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{completedTasks}/{totalTasks} tasks</Typography>
            {estimatedLaunch && launchReason !== 'live' && (
              <Typography variant="body2" color="text.secondary">
                Est. launch: <strong style={{ color: '#ff680d' }}>{new Date(estimatedLaunch + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</strong>
              </Typography>
            )}
            {launchReason === 'live' && <Chip label="LIVE" size="small" color="success" sx={{ fontWeight: 700 }} />}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
            href={`/onboarding/${clientSlug}`} target="_blank">
            Client View
          </Button>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" value={progress} size={56} thickness={5}
              sx={{ color: progress === 100 ? 'success.main' : 'primary.main' }} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" fontWeight={700}>{progress}%</Typography>
            </Box>
          </Box>
        </Stack>
      </Stack>

      {/* Client Onboarding Status */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Client Onboarding Progress</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {completionSteps.map((step) => (
              <Chip
                key={step.key}
                label={step.label}
                size="small"
                icon={step.completed ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                color={step.completed ? 'success' : 'default'}
                variant={step.completed ? 'filled' : 'outlined'}
                sx={{ fontSize: 12 }}
              />
            ))}
          </Stack>
          {/* Client action items (pending) */}
          {clientActions.filter((a) => !a.completed).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="warning.main" fontWeight={600}>Waiting on client:</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                {clientActions.filter((a) => !a.completed).map((a) => (
                  <Chip key={a.key} label={a.label} size="small" color="warning" variant="outlined" sx={{ fontSize: 11 }} />
                ))}
              </Stack>
            </Box>
          )}
          {/* Lead Delivery Status */}
          {(() => {
            const ld = clientActions.find((a) => a.key === 'lead_delivery');
            if (!ld) return null;
            let icon, color, text;
            if (ld.completed) {
              icon = <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />;
              color = 'success.main';
              text = `Lead Delivery: Confirmed${ld.confirmed_by ? ` by ${ld.confirmed_by}` : ''}`;
            } else if (ld.tested) {
              icon = <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
              color = 'warning.main';
              text = 'Lead Delivery: Test passed — awaiting client confirmation';
            } else if (ld.has_url) {
              icon = <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
              color = 'warning.main';
              text = 'Lead Delivery: Webhook configured — awaiting test';
            } else {
              icon = <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
              color = 'text.secondary';
              text = 'Lead Delivery: Awaiting webhook setup';
            }
            return (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {icon}
                  <Typography variant="caption" fontWeight={600} sx={{ color }}>{text}</Typography>
                </Stack>
              </Box>
            );
          })()}

          {selfbookCalendar && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                <Typography variant="caption" fontWeight={600}>Self-Book Calendar:</Typography>
                <Typography variant="caption" color="primary" component="a" href={selfbookCalendar.url} target="_blank" rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline' }}>
                  {selfbookCalendar.url}
                </Typography>
              </Stack>
            </Box>
          )}
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="primary" component="a"
                href={`/projections/${clientSlug}/client-view`} target="_blank" rel="noopener noreferrer"
                sx={{ textDecoration: 'underline', fontWeight: 600 }}>
                View Projections
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" size="small" startIcon={<RocketLaunchIcon />} onClick={() => setFbDialogOpen(true)}>
          Build FB Campaigns
        </Button>
        <Button variant="outlined" size="small" startIcon={<LinkIcon />} href={paths.webhookSetup(clientSlug)} target="_blank">
          Lead Delivery
        </Button>
        <Button variant="outlined" size="small" onClick={() => { setCreativeAds([]); setCreativeVerticalId(''); setCreativeUploadOpen(true); }}>
          Upload Creative
        </Button>
        <Button variant="outlined" size="small" startIcon={<LinkIcon />}
          onClick={() => { setLpUrl(''); setLpVerticalId(''); setLpName(''); setLpDialogOpen(true); }}>
          Add Landing Page
        </Button>
      </Stack>

      <Grid container spacing={2.5}>
        {/* Left: Creative + Landing Pages */}
        <Grid item xs={12} md={7}>
          {/* Client Inputs — Offers + Assets */}
          {onboardingData && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Client Inputs</Typography>

                {/* Assets */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Creative Assets
                  </Typography>
                  {onboardingData.use_stock_assets ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Chip label="Using Stock Media" size="small" color="info" sx={{ fontSize: 11 }} />
                      {onboardingData.assets_folder_link && (
                        <Typography variant="caption" color="primary" component="a" href={onboardingData.assets_folder_link}
                          target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline' }}>
                          Logo/reference link
                        </Typography>
                      )}
                    </Stack>
                  ) : onboardingData.assets_folder_link ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="primary" component="a" href={onboardingData.assets_folder_link}
                        target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'underline', wordBreak: 'break-all', fontSize: 13 }}>
                        {onboardingData.assets_folder_link}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, fontSize: 13 }}>Not provided yet</Typography>
                  )}
                </Box>

                {/* Offers */}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Offers
                  </Typography>
                  {onboardingData.client_offers?.length > 0 ? (
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {(() => {
                        // Group offers by vertical
                        const byVertical = {};
                        onboardingData.client_offers.forEach((o) => {
                          const vName = o.vertical_name || 'General';
                          if (!byVertical[vName]) byVertical[vName] = [];
                          byVertical[vName].push(o);
                        });
                        return Object.entries(byVertical).map(([vName, offers]) => (
                          <Box key={vName}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                              <Chip label={vName} size="small" sx={{ bgcolor: '#111827', color: '#fff', fontWeight: 600, fontSize: 10, height: 20 }} />
                            </Stack>
                            {offers.map((o, i) => (
                              <Typography key={i} variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>
                                <strong>{o.offer_type === 'financing' ? 'Financing: ' : ''}</strong>
                                {o.display_label || o.name || o.custom_label || '—'}
                                {o.financing_details ? ` (${o.financing_details})` : ''}
                              </Typography>
                            ))}
                          </Box>
                        ));
                      })()}
                    </Stack>
                  ) : onboardingData.evergreen_offer ? (
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: 13 }}><strong>Evergreen:</strong> {onboardingData.evergreen_offer}</Typography>
                      {onboardingData.financing_offer && (
                        <Typography variant="body2" sx={{ fontSize: 13 }}><strong>Financing:</strong> {onboardingData.financing_offer}</Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, fontSize: 13 }}>Not provided yet</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Internal Notes for Creative Designer */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Internal Notes</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Notes for the creative team (not visible to client). Include offer details, brand guidelines, tone preferences, etc.
              </Typography>
              <TextField
                multiline minRows={4} fullWidth size="small"
                placeholder="e.g., Client wants bold colors, use their tagline 'Quality You Can Trust', emphasize financing offer in ads..."
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
              />
              <Button
                size="small" variant="contained" sx={{ mt: 1.5 }}
                onClick={handleSaveInternalNotes}
                disabled={notesSaving}
              >
                {notesSaving ? 'Saving...' : 'Save Notes'}
              </Button>
            </CardContent>
          </Card>

          {/* Creative Reviews */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>Ad Creative</Typography>
                <Button size="small" onClick={() => { setCreativeAds([]); setCreativeVerticalId(''); setCreativeUploadOpen(true); }}>
                  + Upload
                </Button>
              </Stack>
              {creativeReviews.length === 0 ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">No creative uploaded yet.</Typography>
                  <Button size="small" variant="outlined" color="success" sx={{ alignSelf: 'flex-start', fontSize: 11 }}
                    onClick={async () => {
                      try {
                        await defaultClient.request({ url: 'tasks/onboarding-tasks/bulk-complete/', method: 'POST',
                          payload: { client_slug: clientSlug, template_keys: ['creative_upload_and_review', 'fb_upload_ad_creative'] } });
                        toast.success('Creative step marked complete'); loadData();
                      } catch { toast.error('Failed'); }
                    }}>
                    Mark Creative Complete (handled externally)
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {creativeReviews.map((batch) => {
                    const allAssets = batch.assets || [];
                    const needsRevision = allAssets.filter((a) => a.asset_status === 'changes_requested');
                    const approved = allAssets.filter((a) => a.asset_status === 'approved');
                    const pending = allAssets.filter((a) => a.asset_status === 'pending');
                    return (
                      <Box key={batch.id}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {batch.vertical_name && <Chip label={batch.vertical_name} size="small" sx={{ bgcolor: '#111827', color: '#fff', fontWeight: 600, fontSize: 11 }} />}
                            <Chip label={batch.status_display} size="small"
                              color={batch.status.includes('approved') ? 'success' : batch.status === 'changes_requested' ? 'warning' : 'info'} />
                            <Typography variant="caption" color="text.secondary">
                              {approved.length}/{allAssets.length} approved
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.75}>
                            {!batch.client_notified_at && pending.length > 0 && (
                              <Button size="small" variant="outlined" onClick={async () => {
                                try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/${batch.id}/notify/`, method: 'POST' }); toast.success('Client notified!'); refreshCreative(); } catch { toast.error('Failed'); }
                              }}>Notify</Button>
                            )}
                            {needsRevision.length > 0 && (
                              <Button size="small" variant="contained" component="label">
                                Revisions ({needsRevision.length})
                                <input type="file" hidden multiple accept="image/*,video/*" onChange={async (e) => {
                                  const files = Array.from(e.target.files || []);
                                  for (let i = 0; i < Math.min(files.length, needsRevision.length); i++) {
                                    const fd = new FormData(); fd.append('file', files[i]);
                                    try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/asset/${needsRevision[i].id}/revision/`, method: 'POST', payload: fd, options: { headers: { 'Content-Type': 'multipart/form-data' } } }); } catch {}
                                  }
                                  toast.success('Revisions uploaded'); refreshCreative(); e.target.value = '';
                                }} />
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                        <Stack spacing={0.75}>
                          {allAssets.map((asset) => (
                            <Stack key={asset.id} direction="row" spacing={1.5} alignItems="center"
                              sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: asset.asset_status === 'approved' ? '#f0fdf4' : asset.asset_status === 'changes_requested' ? '#fff8f0' : '#fff' }}>
                              <Box sx={{ width: 48, height: 48, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: '#f5f5f5' }}>
                                {asset.asset_type === 'video' ? (
                                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#222' }}>
                                    <Typography sx={{ color: '#fff', fontSize: 14 }}>&#9654;</Typography>
                                  </Box>
                                ) : (
                                  <img src={asset.file_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{asset.label || 'Ad'}</Typography>
                                  <Chip label={asset.asset_status} size="small"
                                    color={asset.asset_status === 'approved' ? 'success' : asset.asset_status === 'changes_requested' ? 'warning' : 'default'}
                                    sx={{ fontSize: 10, height: 18 }} />
                                </Stack>
                                {(asset.comments || []).length > 0 && (
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontStyle: 'italic', fontSize: 11 }}>
                                    {(() => { const last = asset.comments[asset.comments.length - 1]; return `${last.author}: ${last.text}`; })()}
                                  </Typography>
                                )}
                              </Box>
                              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                {asset.asset_status !== 'approved' && (
                                  <Button size="small" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async (e) => {
                                    e.stopPropagation();
                                    try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/asset/${asset.id}/action/`, method: 'POST', payload: { action: 'approve' } }); toast.success('Approved'); refreshCreative(); } catch { toast.error('Failed'); }
                                  }}>Approve</Button>
                                )}
                                {asset.asset_status !== 'pending' && (
                                  <Button size="small" color="warning" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async (e) => {
                                    e.stopPropagation();
                                    try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/asset/${asset.id}/action/`, method: 'POST', payload: { action: 'reset' } }); toast.success('Reset to pending'); refreshCreative(); } catch { toast.error('Failed'); }
                                  }}>Reset</Button>
                                )}
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Landing Pages */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>Landing Pages</Typography>
                <Button size="small" onClick={() => { setLpUrl(''); setLpVerticalId(''); setLpName(''); setLpDialogOpen(true); }}>
                  + Add
                </Button>
              </Stack>
              {landingPages.length === 0 ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">No landing pages added yet.</Typography>
                  <Button size="small" variant="outlined" color="success" sx={{ alignSelf: 'flex-start', fontSize: 11 }}
                    onClick={async () => {
                      try {
                        await defaultClient.request({ url: 'tasks/onboarding-tasks/bulk-complete/', method: 'POST',
                          payload: { client_slug: clientSlug, template_keys: ['lp_client_approval', 'landing_page_approval'] } });
                        toast.success('LP step marked complete'); loadData();
                      } catch { toast.error('Failed'); }
                    }}>
                    Mark LP Complete (handled externally)
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {landingPages.map((lp) => {
                    const comments = lp.comments || [];
                    const lastComment = comments.length ? comments[comments.length - 1] : null;
                    return (
                      <Stack key={lp.id} direction="row" spacing={1.5} alignItems="center"
                        sx={{
                          p: 1.5, border: '1px solid', borderRadius: 1,
                          borderColor: lp.approved ? 'success.main' : lp.status === 'changes_requested' ? '#f59e0b' : '#e0e0e0',
                          bgcolor: lp.approved ? '#f0fdf4' : lp.status === 'changes_requested' ? '#fffbeb' : '#fff',
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip label={lp.vertical_name || 'General'} size="small" sx={{ bgcolor: '#111827', color: '#fff', fontWeight: 600, fontSize: 11 }} />
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{lp.name}</Typography>
                            <Chip label={lp.approved ? 'Approved' : lp.status === 'changes_requested' ? 'Changes' : 'Pending'} size="small"
                              color={lp.approved ? 'success' : lp.status === 'changes_requested' ? 'warning' : 'default'} sx={{ fontSize: 10, height: 18 }} />
                          </Stack>
                          <Typography variant="caption" color="primary" component="a" href={lp.url} target="_blank" rel="noopener noreferrer"
                            sx={{ textDecoration: 'underline', wordBreak: 'break-all', fontSize: 11 }}>{lp.url}</Typography>
                          {lastComment && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontStyle: 'italic', fontSize: 11 }}>
                              {`${lastComment.author}: ${lastComment.text}`}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                          {!lp.approved && (
                            <Button size="small" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async () => {
                              try { await defaultClient.request({ url: `clients/${clientSlug}/onboarding/landing-page/${lp.id}/approve/`, method: 'POST', payload: { action: 'approve' } }); toast.success('Approved'); refreshLandingPages(); loadData(); } catch { toast.error('Failed'); }
                            }}>Approve</Button>
                          )}
                          {lp.approved && (
                            <Button size="small" color="warning" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async () => {
                              try { await defaultClient.request({ url: `clients/${clientSlug}/onboarding/landing-page/${lp.id}/approve/`, method: 'POST', payload: { action: 'reset' } }); toast.success('Reset'); refreshLandingPages(); loadData(); } catch { toast.error('Failed'); }
                            }}>Reset</Button>
                          )}
                          <Button size="small" color="error" sx={{ fontSize: 10, minWidth: 0, px: 1 }} onClick={async () => {
                            try { await defaultClient.request({ url: `app1/landing-pages/${clientSlug}/${lp.id}/`, method: 'DELETE' }); toast.success('Removed'); refreshLandingPages(); } catch { toast.error('Failed'); }
                          }}>Remove</Button>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>

        </Grid>

        {/* Right: Task Checklist */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ position: 'sticky', top: 80 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Task Checklist</Typography>
              <Stack spacing={1.5}>
                {Object.entries(phases).map(([phaseName, phaseTasks]) => {
                  const phaseComplete = phaseTasks.filter((t) => t.status === 'complete').length;
                  const phaseTotal = phaseTasks.length;
                  const phaseProgress = phaseTotal ? Math.round((phaseComplete / phaseTotal) * 100) : 0;

                  return (
                    <Accordion key={phaseName} defaultExpanded={phaseProgress < 100} disableGutters
                      sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px !important', '&:before': { display: 'none' }, overflow: 'hidden' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontSize: 13 }}>{phaseName}</Typography>
                          <Typography variant="caption" color={phaseProgress === 100 ? 'success.main' : 'text.secondary'} fontWeight={600}>
                            {phaseComplete}/{phaseTotal}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, px: 1 }}>
                        <Stack spacing={0}>
                          {phaseTasks.sort((a, b) => (a.template_sort_order || 0) - (b.template_sort_order || 0)).map((task) => {
                            const isAutoComplete = task.status === 'complete' && task.evidence?.auto_completed;
                            return (
                              <Stack key={task.id} direction="row" alignItems="center" spacing={0.5}
                                sx={{ py: 0.5, px: 0.5, borderRadius: 0.5, '&:hover': { bgcolor: 'grey.50' } }}>
                                <Checkbox
                                  checked={task.status === 'complete'}
                                  onChange={() => handleToggleComplete(task)}
                                  disabled={isAutoComplete || saving}
                                  size="small" sx={{ p: 0.25 }}
                                />
                                <Typography variant="body2" sx={{
                                  flex: 1, fontSize: 12,
                                  textDecoration: task.status === 'complete' ? 'line-through' : 'none',
                                  color: task.status === 'complete' ? 'text.disabled' : 'text.primary',
                                }}>
                                  {task.template_title}
                                </Typography>
                                {isAutoComplete && <LockIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
                                {task.status === 'blocked' && <Chip label="Blocked" size="small" color="error" sx={{ fontSize: 9, height: 16 }} />}
                                <Typography
                                  variant="caption" color="primary" sx={{ fontSize: 10, cursor: 'pointer', flexShrink: 0 }}
                                  onClick={() => { setNotesDialog(task.id); setNotesValue(task.notes || ''); }}
                                >
                                  {task.notes ? 'Edit' : 'Note'}
                                </Typography>
                              </Stack>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== DIALOGS ===== */}

      {/* Notes dialog */}
      <Dialog open={!!notesDialog} onClose={() => setNotesDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Task Notes</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} value={notesValue} onChange={(e) => setNotesValue(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialog(null)}>Cancel</Button>
          <Button onClick={handleSaveNotes} variant="contained" disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Landing Page Dialog */}
      <Dialog open={lpDialogOpen} onClose={() => setLpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Landing Page</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Vertical *" value={lpVerticalId} fullWidth onChange={(e) => setLpVerticalId(e.target.value)}>
              <MenuItem value="">Select vertical...</MenuItem>
              {lpVerticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
            <TextField label="Landing Page URL *" placeholder="https://client-lp.leadgurus.com" value={lpUrl} onChange={(e) => setLpUrl(e.target.value)} fullWidth />
            <TextField label="Display Name (optional)" value={lpName} onChange={(e) => setLpName(e.target.value)} fullWidth size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLpDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!lpUrl || !lpVerticalId || lpSaving} onClick={async () => {
            setLpSaving(true);
            try { await defaultClient.request({ url: `app1/landing-pages/${clientSlug}/`, method: 'POST', payload: { url: lpUrl, vertical_id: lpVerticalId, name: lpName } }); toast.success('Added!'); setLpDialogOpen(false); refreshLandingPages(); loadData(); }
            catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
            finally { setLpSaving(false); }
          }}>{lpSaving ? 'Saving...' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Creative Upload Dialog */}
      <Dialog open={creativeUploadOpen} onClose={() => setCreativeUploadOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Ad Creative for Client Review</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">Add images/videos and fill in the ad copy for each.</Typography>
            <TextField select label="Vertical *" value={creativeVerticalId} fullWidth size="small" onChange={(e) => setCreativeVerticalId(e.target.value)} helperText="Which vertical are these ads for?">
              <MenuItem value="">Select vertical...</MenuItem>
              {lpVerticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
            {creativeAds.map((ad, idx) => (
              <Card key={idx} variant="outlined">
                <CardContent sx={{ py: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Ad {idx + 1}: {ad.file?.name || 'No file'}</Typography>
                    <Button size="small" color="error" onClick={() => setCreativeAds((prev) => prev.filter((_, i) => i !== idx))}>Remove</Button>
                  </Stack>
                  <Stack spacing={1.5}>
                    <TextField label="Headline" size="small" fullWidth value={ad.headline} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, headline: e.target.value } : a))} />
                    <TextField label="Primary Text" size="small" fullWidth multiline minRows={2} value={ad.primary_text} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, primary_text: e.target.value } : a))} />
                    <Stack direction="row" spacing={1.5}>
                      <TextField label="Description" size="small" fullWidth value={ad.description} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, description: e.target.value } : a))} />
                      <TextField select label="CTA" size="small" value={ad.cta_type} sx={{ minWidth: 140 }} onChange={(e) => setCreativeAds((prev) => prev.map((a, i) => i === idx ? { ...a, cta_type: e.target.value } : a))}>
                        {[['LEARN_MORE','Learn More'],['SIGN_UP','Sign Up'],['GET_QUOTE','Get Quote'],['CONTACT_US','Contact Us'],['BOOK_NOW','Book Now'],['CALL_NOW','Call Now'],['GET_OFFER','Get Offer']].map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                      </TextField>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
              + Add Creative Files
              <input type="file" hidden multiple accept="image/*,video/*" onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setCreativeAds((prev) => [...prev, ...files.map((f) => ({ file: f, headline: '', primary_text: '', description: '', cta_type: 'LEARN_MORE' }))]);
                e.target.value = '';
              }} />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreativeUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={creativeAds.length === 0 || creativeUploading} onClick={async () => {
            setCreativeUploading(true);
            try {
              const formData = new FormData();
              if (creativeVerticalId) formData.append('vertical_id', creativeVerticalId);
              creativeAds.forEach((ad) => { formData.append('files', ad.file); formData.append('labels', ad.file.name); formData.append('headlines', ad.headline); formData.append('primary_texts', ad.primary_text); formData.append('descriptions', ad.description); formData.append('cta_types', ad.cta_type); });
              const uploadRes = await defaultClient.request({ url: `app1/creative-review/${clientSlug}/upload/`, method: 'POST', payload: formData, options: { headers: { 'Content-Type': 'multipart/form-data' } } });
              setCreativeUploadOpen(false);
              const batchId = uploadRes?.data?.id;
              if (batchId) { try { await defaultClient.request({ url: `app1/creative-review/${clientSlug}/${batchId}/notify/`, method: 'POST' }); toast.success('Uploaded & client notified!'); } catch { toast.success('Uploaded!'); } }
              refreshCreative(); loadData();
            } catch { toast.error('Upload failed'); } finally { setCreativeUploading(false); }
          }}>
            {creativeUploading ? 'Uploading...' : `Upload ${creativeAds.length} Ad${creativeAds.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facebook Campaign Builder dialog */}
      <Dialog open={fbDialogOpen} onClose={() => { setFbDialogOpen(false); setFbResult(null); }} maxWidth="md" fullWidth>
        <DialogTitle><Stack direction="row" alignItems="center" spacing={1}><RocketLaunchIcon color="primary" /><Typography variant="h6">Build Facebook Campaigns</Typography></Stack></DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">Create campaigns, ad sets, and ads in Facebook for <strong>{clientName}</strong>. All campaigns are created <strong>PAUSED</strong>.</Typography>
            <TextField label="Facebook Ad Account ID" placeholder="123456789 or act_123456789" value={fbAccountId} onChange={(e) => setFbAccountId(e.target.value)} fullWidth required />
            <TextField label="Facebook Page ID (optional)" placeholder="489723540880228" value={fbPageId} onChange={(e) => setFbPageId(e.target.value)} fullWidth />
            {fbResult && (
              <Box>
                {fbResult.campaigns?.map((c, i) => (
                  <Card key={i} variant="outlined" sx={{ mb: 1, bgcolor: c.error ? 'error.50' : 'success.50' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>{c.campaign_name || c.vertical}</Typography>
                      {c.campaign_id && <Typography variant="caption" color="text.secondary">ID: {c.campaign_id}</Typography>}
                      {c.error && <Typography variant="caption" color="error"> {c.error}</Typography>}
                    </CardContent>
                  </Card>
                ))}
                {fbResult.errors?.map((e, i) => <Typography key={i} variant="caption" color="error">{e.error}</Typography>)}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFbDialogOpen(false); setFbResult(null); }}>Close</Button>
          <Button variant="outlined" onClick={() => handleFbBuild(true)} disabled={fbBuilding || !fbAccountId}>Preview</Button>
          <Button variant="contained" onClick={() => handleFbBuild(false)} disabled={fbBuilding || !fbAccountId}
            startIcon={fbBuilding ? <CircularProgress size={16} /> : <RocketLaunchIcon />}>
            {fbBuilding ? 'Building...' : 'Build'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
